import React from "react";

const AcceptableUsePolicy = () => (
  <div className="min-h-screen bg-black text-white flex flex-col items-center py-12 px-4">
    <div className="w-full max-w-2xl mx-auto">
      <h1 className="text-4xl font-semibold font-primary text-center mb-2">
        FOZZIL Acceptable Use Policy
      </h1>
      <div className="text-gray-300 italic text-center mb-10 text-lg">
        Effective Date: April 13, 2025
      </div>

      <h2 className="text-2xl font-bold mb-3 mt-10">Overview</h2>
      <p className="mb-6 text-lg leading-relaxed">
        This Acceptable Use Policy (“AUP”) outlines the standards of behavior
        expected from all users of FOZZIL's services — including individuals,
        developers, businesses, and enterprise partners. Our goal is to empower
        users to benefit from our technology while promoting ethical, legal, and
        responsible use of our platforms.
        <br />
        <br />
        By accessing or using any FOZZIL product, service, or platform, you
        agree to adhere to this policy. Failure to comply may result in
        corrective actions, including account suspension or termination, at
        FOZZIL’s discretion.
        <br />
        <br />
        This policy will evolve alongside our services and the broader
        regulatory and technological landscape.
      </p>

      <h2 className="text-2xl font-bold mb-3 mt-10">General Expectations</h2>
      <ul className="mb-8 text-lg list-disc list-inside space-y-2">
        <li>Compliant with the law</li>
        <li>Safe and responsible</li>
        <li>Respectful of individuals and communities</li>
        <li>Aligned with FOZZIL’s ethical guardrails</li>
      </ul>

      <h2 className="text-2xl font-bold mb-3 mt-10">Prohibited Activities</h2>
      <ol className="mb-8 text-lg list-decimal list-inside space-y-4">
        <li>
          <span className="font-bold">Illegal or Unlawful Use</span>
          <p className="ml-6 mt-2 mb-2">
            You may not use FOZZIL services to promote, participate in, or
            facilitate illegal activities. This includes (but is not limited
            to):
          </p>
          <ul className="ml-10 list-disc space-y-1">
            <li>Copyright, trademark, or intellectual property infringement</li>
            <li>Privacy violations, doxxing, or unauthorized data sharing</li>
            <li>
              Use of real or synthetic media for non-consensual pornography or
              deepfakes
            </li>
            <li>Sexual exploitation or endangerment of minors</li>
            <li>
              Misuse of our platform in regulated industries without compliance
            </li>
            <li>Fraud, impersonation, defamation, or financial scams</li>
            <li>Espionage, surveillance, stalking, or cyberattacks</li>
          </ul>
        </li>
        <li>
          <span className="font-bold">Harmful or Dangerous Use</span>
          <p className="ml-6 mt-2 mb-2">
            You may not use FOZZIL services to harm individuals or property.
            Prohibited uses include:
          </p>
          <ul className="ml-10 list-disc space-y-1">
            <li>
              Encouraging or enabling suicide, self-harm, or harm to others
            </li>
            <li>
              Unauthorized actions on behalf of others or impersonating entities
            </li>
            <li>
              Designing or facilitating bioweapons, chemical weapons, or WMDs
            </li>
            <li>
              Promoting violence, destruction, or sabotage of physical assets
            </li>
          </ul>
        </li>
        <li>
          <span className="font-bold">
            Circumvention or Abuse of Safety Measures
          </span>
          <p className="ml-6 mt-2 mb-2">
            FOZZIL includes built-in safeguards to prevent misuse. You may not:
          </p>
          <ul className="ml-10 list-disc space-y-1">
            <li>Attempt to bypass, disable, or exploit these protections</li>
            <li>
              Generate misleading content that appears to be human-generated
              without disclosure
            </li>
            <li>
              Omit attribution when using FOZZIL-generated outputs (e.g.,
              AI-generated text or images)
            </li>
            <li>
              Mislead others about the source or authorship of any AI-generated
              material
            </li>
            <li>
              Conduct red-teaming or adversarial probing without prior, explicit
              approval
            </li>
          </ul>
        </li>
      </ol>

      <h2 className="text-2xl font-bold mb-3 mt-10">
        AI Transparency & Attribution
      </h2>
      <p className="mb-8 text-lg leading-relaxed">
        If FOZZIL products assist you in generating content, you must provide
        appropriate attribution and ensure transparency. Users are expected to
        clearly indicate when material is AI-assisted or fully AI-generated,
        particularly in public, academic, or commercial contexts.
      </p>

      <h2 className="text-2xl font-bold mb-3 mt-10">Protection of Children</h2>
      <p className="mb-8 text-lg leading-relaxed">
        FOZZIL has a zero-tolerance policy for any content involving the
        exploitation of minors. Any suspected child sexual abuse material (CSAM)
        will be reported to the National Center for Missing and Exploited
        Children (NCMEC) or relevant authorities without exception.
      </p>

      <h2 className="text-2xl font-bold mb-3 mt-10">Enforcement</h2>
      <p className="mb-8 text-lg leading-relaxed">
        Violation of this Acceptable Use Policy may lead to:
      </p>
      <ul className="mb-8 text-lg list-disc list-inside space-y-1">
        <li>Warning or content removal</li>
        <li>Temporary or permanent account suspension</li>
        <li>Reporting to legal authorities, if required</li>
      </ul>
      <p className="mb-8 text-lg leading-relaxed">
        FOZZIL reserves the right to investigate violations and take appropriate
        action to protect its users, services, and the broader public.
      </p>

      <h2 className="text-2xl font-bold mb-3 mt-10">Contact</h2>
      <p className="mb-8 text-lg leading-relaxed">
        For clarification, exception requests, or to report violations of this
        policy, contact:
        <br />
        <span className="block mt-2">
          📩{" "}
          <a
            href="mailto:legal@fozzil.in"
            className="underline hover:text-blue-400"
          >
            legal@fozzil.in
          </a>
        </span>
      </p>
    </div>
  </div>
);

export default AcceptableUsePolicy;
